<template>
  <div
    class="page-wrapper"
    data-id="card-page"
    :data-loading="loading ? '1' : '0'"
    :data-saml-footer="samlFooter ? '1' : '0'"
    :data-card-page-done="done ? '1' : '0'"
    :data-page-id="dataId ? dataId : 'card-page'"
  >
    <!--    <NavBar />-->
    <div
      class="card-page flex flex-1 flex-col"
      :class="wide ? 'card-page-wide' : ''"
      data-id="card-page"
    >
      <!-- Content -->
      <div
        class="h-100 flex flex-1 items-center justify-center py-8 lg:py-16"
        data-id="card-wrapper"
      >
        <div
          :class="{
            'mx-auto w-full': true,
            'max-w-xl': !showFeature,
            'max-w-4xl': showFeature,
          }"
        >
          <Flash class="mb-6 w-full" />
          <LoadingCard v-if="loading" />
          <slot v-else name="card">
            <div class="xcontainer flex flex-col gap-24 lg:flex-row">
              <!-- CARD -->
              <form
                class="sm:mx-auto sm:w-full sm:max-w-[480px]"
                data-id="card-page-form"
                @submit.prevent.stop="submit"
              >
                <!-- header -->
                <div
                  class="mb-6 sm:mx-auto sm:w-full sm:max-w-md"
                  v-if="!noHeader && !done"
                >
                  <NuxtLink to="/">
                    <img
                      class="h-6 w-auto"
                      src="/mailslurp-logo.svg"
                      alt="MailSlurp"
                    />
                  </NuxtLink>
                  <h2
                    class="mt-6 text-3xl font-bold leading-9 tracking-tight text-gray-900"
                  >
                    {{ cardTitle || title }}
                  </h2>
                  <p
                    class="text-md mt-1 leading-6 text-gray-500"
                    v-if="description"
                  >
                    {{ description }}
                  </p>
                </div>
                <CardPageCard
                  class="flex-1"
                  :show-current-session-banner="showCurrentSessionBanner"
                >
                  <div class="flex w-full flex-col gap-6">
                    <!-- banner icon -->
                    <CardPageBannerIcon :banner-icon="bannerIcon" />
                    <!-- card body -->
                    <article class="flex flex-col gap-6">
                      <!-- body -->
                      <section>
                        <!-- loading -->
                        <!-- done -->
                        <DoneImage
                          v-if="done"
                          :done="done"
                          :done-image="doneImage"
                          :done-message="doneMessage"
                          :done-strong="doneStrong"
                        />
                        <!-- content -->
                        <div v-else>
                          <!-- form -->
                          <div class="flex flex-col gap-3">
                            <!-- content -->
                            <slot />
                            <!-- footer -->
                            <section>
                              <footer
                                class="mb-0 flex flex-col items-start justify-start gap-2"
                                v-if="!noSubmit"
                              >
                                <div class="w-full">
                                  <Btn
                                    class="w-full"
                                    tag="button"
                                    :variant="Variant.success"
                                    data-loading="loading"
                                    :data-id="
                                      primaryDataId || 'card-page-submit'
                                    "
                                    size="lg"
                                    name="submit"
                                    type="submit"
                                    :icon="submitIcon"
                                  >
                                    {{ submitText || 'Next' }}
                                  </Btn>
                                  <Btn
                                    class="w-full"
                                    v-if="submitText2"
                                    tag="button"
                                    size="lg"
                                    :variant="Variant.secondary"
                                    data-loading="loading"
                                    data-id="card-page-submit-secondary"
                                    name="button2"
                                    type="button"
                                    @click="onSubmit2"
                                  >
                                    <span>
                                      {{ submitText2 }}
                                    </span>
                                    <Icon
                                      v-if="submitIcon2"
                                      :icon="submitIcon2"
                                    />
                                  </Btn>
                                  <slot name="after" />
                                </div>
                              </footer>
                            </section>
                          </div>
                          <!-- sso -->
                        </div>
                      </section>
                    </article>
                    <!-- if not done footer -->
                  </div>
                </CardPageCard>
                <div
                  class="mt-6 sm:mx-auto sm:w-full sm:max-w-md"
                  v-if="!done && !loading && (secondaryTo || ctaTo)"
                >
                  <CardPageFooter
                    class="mt-6"
                    :cta-text="ctaText"
                    :cta-to="ctaTo"
                    :secondary-data-id="secondaryDataId"
                    :secondary-text="secondaryText"
                    :secondary-icon="secondaryIcon"
                    :secondary-to="secondaryTo"
                  />
                </div>
              </form>
              <slot v-if="showFeature" name="feature" />
            </div>
          </slot>
          <!-- saml -->
          <div
            class="card-page-card-width mx-auto flex flex-row items-center justify-center gap-5 pt-8"
            v-if="!done && !loading && samlFooter"
            data-el="cp:saml-footer"
          >
            <a
              class="text-center text-sm leading-6 text-gray-500 underline hover:no-underline"
              v-for="link in extraLogins"
              :key="link.to"
              :href="link.to"
              >{{ link.text }}</a
            >
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import Icon from '../components/form/Icon.vue';
import Flash from './Flash.vue';
import DoneImage from '../components/card/DoneImage.vue';
import CardPageFooter from '../components/page/CardPageFooter.vue';
import CardPageBannerIcon from '../components/page/CardPageBannerIcon.vue';
import CardPageCard from '../components/tw/CardPageCard.vue';
import { computed, useSlots } from 'vue';
import Btn from '../components/form/Btn.vue';
import LoadingCard from '../components/LoadingCard.vue';
import { Variant } from '../store/types';
import type { MsIcon } from '../lib/services/types';

const slots = useSlots();

const showFeature = computed(() => {
  return slots.feature && !props.loading && !props.done;
});

const extraLogins = computed(() => {
  return [
    {
      to: 'https://enterprise.mailslurp.com/',
      text: 'Enterprise SAML/SSO login' as string,
    },
    {
      to: '/organization',
      text: 'Enterprise email login' as string,
    },
  ];
});

const props = withDefaults(
  defineProps<{
    showCurrentSessionBanner?: boolean;
    loading?: boolean;
    noSubmit?: boolean;
    wide?: boolean;
    noHeader?: boolean;
    samlFooter?: boolean;
    done?: boolean;
    doneImage?: string;
    doneStrong?: string;
    doneMessage?: string;
    secondaryDataId?: string;
    primaryDataId?: string;
    ctaText?: string;
    ctaTo?: string;
    submitIcon?: MsIcon;
    submitIcon2?: MsIcon;
    secondaryIcon?: MsIcon;
    secondaryText?: string;
    dataId?: string;
    secondaryTo?: string;
    bannerIcon?: MsIcon;
    cardTitle?: string;
    title?: string;
    description?: string;
    submitText?: string;
    submitText2?: string;
  }>(),
  {
    helpLink: '',
    doneImage: '',
    doneStrong: '',
    doneMessage: '',
    secondaryDataId: '',
    primaryDataId: '',
    ctaText: '',
    ctaTo: '',
    button2Text: '',
    button2To: '',
    submitIcon: undefined,
    submitIcon2: undefined,
    secondaryIcon: undefined,
    secondaryText: '',
    dataId: '',
    secondaryTo: '',
    bannerIcon: undefined,
    cardTitle: '',
    title: '',
    description: '',
    submitText: '',
    submitText2: '',
  },
);

const emit = defineEmits<{ (e: 'submit'): void; (e: 'submit2'): void }>();

const submit = () => {
  emit('submit');
};

const onSubmit2 = () => {
  emit('submit2');
};
</script>
